import { useEffect, useState } from 'react'

/**
 * Airport lookup, by IATA code.
 *
 * A flight record stores only `origin` and `destination` as three-letter
 * codes, so everything else — the timezone a departure time must be read in,
 * the city, the country — comes from here. The Swift side does the same thing
 * through `AirportService`.
 *
 * 6,438 airports is 428KB, which has no business being in the JS bundle. It's
 * a static asset, fetched once on the first screen that needs it and then held
 * for the session. Times render as "—" until it lands rather than briefly
 * rendering in the wrong zone.
 */

/** [timezone, city, countryCode, name] — a tuple to keep the payload small. */
type AirportTuple = [string, string, string, string]

export interface Airport {
  iata: string
  timeZone: string
  city: string
  countryCode: string
  name: string
}

let index: Record<string, AirportTuple> | null = null
let inFlight: Promise<Record<string, AirportTuple>> | null = null

async function load(): Promise<Record<string, AirportTuple>> {
  if (index) return index
  if (inFlight) return inFlight

  inFlight = fetch('/assets/airports.json')
    .then((response) => {
      if (!response.ok) throw new Error(`airports.json ${response.status}`)
      return response.json() as Promise<Record<string, AirportTuple>>
    })
    .then((loaded) => {
      index = loaded
      return loaded
    })
    .catch((error) => {
      console.error('[airports] failed to load', error)
      // Cache the empty result rather than retrying on every render — a
      // missing file shouldn't turn into a request storm.
      index = {}
      return index
    })

  return inFlight
}

/** Synchronous lookup. Returns null until the index has loaded. */
export function lookupAirport(iata: string | null | undefined): Airport | null {
  if (!iata || !index) return null
  const entry = index[iata.toUpperCase()]
  if (!entry) return null
  return {
    iata: iata.toUpperCase(),
    timeZone: entry[0],
    city: entry[1],
    countryCode: entry[2],
    name: entry[3],
  }
}

/** The zone a code's local times should be read in, or null if unknown. */
export function airportTimeZone(iata: string | null | undefined): string | null {
  return lookupAirport(iata)?.timeZone ?? null
}

/**
 * Ensures the index is loaded and re-renders when it lands.
 *
 * Call this in any screen that formats a flight time. Without it the first
 * render has no zone and would fall back to the browser's, which is exactly
 * the class of bug that puts a Melbourne arrival on the wrong day.
 */
export function useAirports(): boolean {
  const [ready, setReady] = useState(() => index !== null)

  useEffect(() => {
    if (index) {
      setReady(true)
      return
    }
    let alive = true
    void load().then(() => {
      if (alive) setReady(true)
    })
    return () => {
      alive = false
    }
  }, [])

  return ready
}

/** Codes matching a query, for the airport picker. Capped for the dropdown. */
export function searchAirports(query: string, limit = 8): Airport[] {
  if (!index) return []
  const needle = query.trim().toLowerCase()
  if (needle.length < 2) return []

  const results: Airport[] = []
  for (const [iata, entry] of Object.entries(index)) {
    if (
      iata.toLowerCase().startsWith(needle) ||
      entry[1].toLowerCase().includes(needle) ||
      entry[3].toLowerCase().includes(needle)
    ) {
      results.push({
        iata,
        timeZone: entry[0],
        city: entry[1],
        countryCode: entry[2],
        name: entry[3],
      })
      if (results.length >= limit * 3) break
    }
  }

  // Exact code matches first — typing "AKL" should not rank behind a city
  // whose name happens to contain those letters.
  return results
    .sort((a, b) => {
      const aExact = a.iata.toLowerCase() === needle ? 0 : 1
      const bExact = b.iata.toLowerCase() === needle ? 0 : 1
      return aExact - bExact
    })
    .slice(0, limit)
}
